<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { fetchDungeons, type DungeonCatalogEntry, type DungeonRunState } from '../game/dungeons-api';
import { heistArt } from '../game/art';
import { confirmAction } from '../game/confirm';
import FightScreen from './FightScreen.vue';
import { startHeist, type FightView } from '../game/mobfight-api';
import cashIcon from '../assets/fimbulwake/icons/cash.webp';
import runesIcon from '../assets/fimbulwake/icons/runes.webp';
import nerveIcon from '../assets/fimbulwake/icons/nerve.webp';
import { t } from '../i18n';

// Bubble nerve/cash/xp changes up so HeroScreen refreshes the header.
// `nerve` comes DOWN from the shell (same live value the header chip shows) so
// the door can warn about an unaffordable raid BEFORE the server bounces it —
// QA CU-86cb9yd3p follow-up: "no proper warning when entering a raid with no
// stamina". `nerveRegenPerMin` prices the wait in the warning.
const props = defineProps<{
  heroClass?: string;
  nerve?: { current: number; max: number };
  nerveRegenPerMin?: number;
}>();
const emit = defineEmits<{
  (e: 'updated'): void;
  /** "Get fury" from the no-fury warning — the shell routes it to the Supplies
   *  stall, the same landing the header's nerve chip gives. */
  (e: 'supplies'): void;
}>();

const loading = ref(true);
const error = ref('');
const dungeons = ref<DungeonCatalogEntry[]>([]);
const run = ref<DungeonRunState | null>(null);

// The dungeon currently in the interactive tactical arena (null = catalog view).
const heistTarget = ref<string | null>(null);
const heistInitial = ref<FightView | null>(null); // the run we started, handed to FightScreen

const activeDungeon = computed(() =>
  run.value ? dungeons.value.find((d) => d.id === run.value!.dungeonId) ?? null : null,
);

const hpPct = computed(() =>
  run.value && run.value.maxHp > 0
    ? Math.max(0, Math.min(100, Math.round((run.value.hp / run.value.maxHp) * 100)))
    : 0,
);
/* aurora hale, ember hurt, blood critical — the same three tones the hero card and
   the ward chests use, so one scale reads across the whole game. */
const hpTone = computed(() =>
  hpPct.value > 50 ? 'var(--color-aurora)' : hpPct.value > 25 ? 'var(--color-ember)' : 'var(--color-error)');

async function load() {
  try {
    const res = await fetchDungeons();
    dungeons.value = res.dungeons;
    run.value = res.activeRun;
  } catch (e) {
    error.value = (e as Error).message;
  } finally {
    loading.value = false;
  }
}

onMounted(load);

/** Open the tactical arena on a dungeon — starts a fresh run, or resumes an
 *  active one (the backend's /mobfight/heist handles both). Fresh runs charge
 *  nerve at the door, so they go through the confirmation card first. */
/** Minutes until the crew can afford `cost` fury, or null when unknowable. */
function furyEtaMin(cost: number): number | null {
  const cur = props.nerve?.current ?? null;
  const perMin = props.nerveRegenPerMin ?? 0;
  if (cur === null || perMin <= 0) return null;
  return Math.ceil((cost - cur) / perMin);
}

async function begin(id: string) {
  error.value = '';
  const resuming = run.value?.status === 'ACTIVE' && run.value.dungeonId === id;
  if (!resuming) {
    const d = dungeons.value.find((x) => x.id === id);

    // Too little fury for the door charge? Say so NOW, in a dialog, with the
    // shortfall and the wait — not after the confirm, as a small red line at
    // the top of the panel the player has already scrolled past (QA
    // CU-86cb9yd3p follow-up). The known-broke case never reaches the server;
    // the catch below stays for the race where regen display and server clock
    // disagree by a point.
    if (d && props.nerve && props.nerve.current < d.nerveCost) {
      const eta = furyEtaMin(d.nerveCost);
      const toSupplies = await confirmAction({
        kicker: t('dungeons.confirm.kicker'),
        title: t('dungeons.noFury.title'),
        icon: heistArt(id) || undefined,
        rows: [
          { label: t('dungeons.confirm.doorCharge'), value: String(d.nerveCost), icon: nerveIcon },
          { label: t('dungeons.noFury.yours'), value: `${props.nerve.current}/${props.nerve.max}`, icon: nerveIcon, tone: 'error' },
        ],
        note: eta !== null ? t('dungeons.noFury.eta', { min: eta }) : t('dungeons.noFury.note'),
        // The way OUT, not a dead "OK": confirm routes to the Supplies stall
        // (the same landing the header's nerve chip gives), cancel just closes.
        confirmLabel: t('dungeons.noFury.toSupplies'),
        cancelLabel: t('common.close'),
        danger: true,
      });
      if (toSupplies) emit('supplies');
      return;
    }

    const ok = await confirmAction({
      kicker: t('dungeons.confirm.kicker'),
      title: d ? t('dungeons.confirm.title', { name: d.name }) : t('dungeons.confirm.title', { name: '' }),
      icon: heistArt(id) || undefined,
      rows: d ? [
        { label: t('dungeons.confirm.rooms'), value: String(d.totalStages), tone: 'dim' },
        { label: t('dungeons.confirm.doorCharge'), value: String(d.nerveCost), icon: nerveIcon },
        // Your side of the charge, right next to it — affordable reads dim,
        // and the whole dialog above replaces this row when it is not.
        ...(props.nerve ? [{ label: t('dungeons.noFury.yours'), value: `${props.nerve.current}/${props.nerve.max}`, icon: nerveIcon, tone: 'dim' as const }] : []),
        { label: t('dungeons.confirm.fullTake'), value: d.rewardCash.toLocaleString(), icon: cashIcon, tone: 'success' },
      ] : [],
      note: t('dungeons.confirm.note'),
      confirmLabel: t('dungeons.confirm.confirm'),
    });
    if (!ok) return;
  }
  // Start the run HERE, not inside the fight screen: opening it is what checks the
  // door charge (nerve), the crew's HP and whether a run is already ACTIVE. Doing
  // it here keeps "Not enough nerve" on this panel instead of throwing the player
  // onto a fight screen with nothing but an error on it.
  try {
    heistInitial.value = await startHeist(id);
    heistTarget.value = id;
  } catch (e) {
    error.value = (e as Error).message;
  }
}

/** The tactical arena closed — refresh the catalog/run + let the header refresh. */
function onHeistDone() {
  heistTarget.value = null;
  void load();
  emit('updated');
}

/** 72h post-clear cooldown (server-enforced; QA: repeat-heist money glitch).
 *  QA 15.08 asked for LIVE timers on raids too, same as the wards — a 1s tick
 *  (Districts.vue's exact pattern) so the label visibly counts down, with
 *  M:SS granularity for the final hour instead of a frozen "1h". */
const nowTick = ref(Date.now());
const _tickTimer = window.setInterval(() => { nowTick.value = Date.now(); }, 1_000);
onUnmounted(() => clearInterval(_tickTimer));
function coolingDown(d: DungeonCatalogEntry): boolean {
  return d.cooldownUntil != null && d.cooldownUntil > nowTick.value;
}
/** The wait, always with a component that visibly moves. Above two days this
 *  used to collapse to a bare "3d" that sat unchanged for 23 hours — a live
 *  timer that reads as a frozen one (QA 86cb8zjzx), so the days now carry their
 *  hours and minutes. Under an hour it goes to M:SS. */
function cooldownLabel(d: DungeonCatalogEntry): string {
  const ms = Math.max(0, (d.cooldownUntil ?? 0) - nowTick.value);
  const totalMin = Math.floor(ms / 60_000);
  const days = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (days > 0) return `${days}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  const sec = Math.floor((ms % 60_000) / 1_000);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function ctaLabel(d: DungeonCatalogEntry): string {
  if (d.locked) return t('dungeons.offLimits', { level: d.unlockLevel });
  if (run.value && run.value.status === 'ACTIVE') {
    return run.value.dungeonId === d.id ? t('dungeons.backToRaid') : t('dungeons.finishFirst');
  }
  if (coolingDown(d)) return t('dungeons.smoldering', { time: cooldownLabel(d) });
  return t('dungeons.confirm.confirm');
}
const ctaDisabled = (d: DungeonCatalogEntry) =>
  d.locked || coolingDown(d) ||
  (!!run.value && run.value.status === 'ACTIVE' && run.value.dungeonId !== d.id);

/* ── Presentational only: the "Longship's Course" danger scale. Maps a stop's
   position in the catalog (already ordered cheapest-door-first by the backend)
   to a 0%→100% cold→ember reading, used purely for CSS tint — the route boss,
   the ember pool at a scene's foot, the bound-frame rim, the art's saturation.
   Touches no state, no ordering, no run/bail/bank logic. */
function dangerPct(i: number, total: number): string {
  const t = total > 1 ? i / (total - 1) : 0;
  return `${Math.round(t * 100)}%`;
}
</script>

<template>
  <section>
    <!-- banner -->
    <header class="scr-head">
      <span class="scr-head-win"><img :src="heistArt('speakeasy_raid')" alt="" /></span>
      <div class="scr-head-txt">
        <h1>{{ t('dungeons.title') }}</h1>
        <p>{{ t('dungeons.sub') }}</p>
      </div>
    </header>
    <p class="font-body italic text-text-dim text-sm mb-6">
      Fight the raid hall by hall. Take your share of every hall you clear and turn back with it —
      or press on to the last door and carry off the whole hoard.
    </p>

    <p v-if="error" class="mb-4 text-sm text-error">{{ error }}</p>
    <p v-if="loading" class="text-text-dim">{{ t('dungeons.loading') }}</p>

    <!-- ACTIVE RUN — the job in progress -->
    <div
      v-if="!loading && run && run.status === 'ACTIVE' && activeDungeon"
      class="rc-log bound-frame relative mb-8 overflow-hidden"
    >
      <img v-if="heistArt(activeDungeon.id)" :src="heistArt(activeDungeon.id)" alt=""
           class="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div class="absolute inset-0" style="background:linear-gradient(90deg,rgba(6,10,18,0.94),rgba(6,10,18,0.68))"></div>

      <div class="relative flex flex-wrap items-center gap-x-8 gap-y-4">
        <div class="min-w-0">
          <div class="rc-log-kicker">{{ t('dungeons.inProgress') }}</div>
          <h2 class="rc-log-title">{{ activeDungeon.name }}</h2>
        </div>

        <!-- room pips -->
        <div class="flex items-center gap-2">
          <span class="rc-log-lbl">{{ t('dungeons.confirm.rooms') }}</span>
          <span class="flex items-center gap-[6px]">
            <i v-for="n in run.totalStages" :key="n" class="rc-pip"
               :class="{ 'rc-pip-clear': n < run.stage, 'rc-pip-now': n === run.stage }"></i>
          </span>
          <span class="font-heading text-sm text-text tabular-nums">{{ run.stage }}/{{ run.totalStages }}</span>
        </div>

        <!-- crew hp -->
        <div class="flex items-center gap-2 min-w-[180px] flex-1 max-w-[300px]">
          <span class="rc-log-lbl shrink-0">{{ t('dungeons.crewLabel') }}</span>
          <!-- the raid party's health is the SAME gauge as everywhere else: boards
               in an iron channel. Was a bespoke track+fill, the last hand-rolled
               meter left in the build. -->
          <div class="bar-shield flex-1" :class="{ 'is-splintered': hpPct <= 30 }"
               :style="{ '--sh-pct': hpPct, '--sh-tone': hpTone }">
            <div class="bar-shield-fill"></div>
          </div>
          <span class="font-heading text-sm tabular-nums shrink-0" :style="{ color: hpTone }">{{ run.hp }}/{{ run.maxHp }}</span>
        </div>

        <button
          @click="begin(run.dungeonId)"
          class="btn-forge btn-forge--rune ml-auto"
        >{{ t('dungeons.backToRaid') }}</button>
      </div>
    </div>

    <!-- ══ THE LONGSHIP'S COURSE ═══════════════════════════════════════════════
         WAS two `grid-cols` of heist posters — a catalogue you scan, not a route
         you read. Raids are now stops laid along ONE carved channel: a single
         `56px 1fr` grid shared by every row, so the channel's x-position is one
         value for the whole page rather than four eyeballed offsets. A struck
         boss on the channel marks each stop; the boss (and the stop's own rim)
         reads cold near the top of the course and warms to ember toward the
         foot, because the raids are already catalogued cheapest-door-first —
         so "further down the course" IS "further into danger". ═══════════════ -->
    <div v-if="!loading" class="rc-route">
      <template v-for="(d, i) in dungeons" :key="d.id">
        <div class="rc-chan" :style="{ '--dp': dangerPct(i, dungeons.length) }" aria-hidden="true">
          <span class="rc-boss" :class="{ 'is-here': run && run.status === 'ACTIVE' && run.dungeonId === d.id }"></span>
        </div>

        <article
          class="rc-stop bound-frame surf-plank relative"
          :style="{ '--dp': dangerPct(i, dungeons.length) }"
          :class="{
            'is-sealed': d.locked,
            'is-smoldering': !d.locked && coolingDown(d),
            'is-here': run && run.status === 'ACTIVE' && run.dungeonId === d.id,
          }"
        >
          <div class="rc-stop-body">
            <!-- the scene: the raid's own painted plate, framed and inset — never
                 a full-bleed backdrop behind the whole stop. -->
            <div class="rc-scene">
              <img v-if="heistArt(d.id)" :src="heistArt(d.id)" :alt="d.name" />
              <span class="rc-scene-scrim" aria-hidden="true"></span>
              <span class="rc-pool" aria-hidden="true"></span>
              <span v-if="d.locked" class="rc-frost" aria-hidden="true"></span>
              <span v-else-if="coolingDown(d)" class="rc-embers" aria-hidden="true"></span>
              <span class="rc-rooms">{{ d.totalStages }} rooms</span>
              <h2 class="rc-name">{{ d.name }}</h2>
            </div>

            <div class="rc-info">
              <p class="rc-blurb">{{ d.blurb }}</p>

              <div class="rc-terms">
                <div class="rc-term">
                  <span class="rc-term-k">{{ t('dungeons.confirm.doorCharge') }}</span>
                  <span class="rc-term-v"><img :src="nerveIcon" alt="" class="rc-term-icon" />{{ d.nerveCost }}</span>
                </div>
                <div class="rc-term">
                  <span class="rc-term-k">{{ t('dungeons.confirm.fullTake') }}</span>
                  <span class="rc-term-v rc-term-v--coin"><img :src="cashIcon" alt="" class="rc-term-icon" />{{ d.rewardCash.toLocaleString() }}</span>
                </div>
                <div class="rc-term">
                  <span class="rc-term-k">{{ t('dungeons.sagaXp') }}</span>
                  <span class="rc-term-v">᛭ {{ d.rewardXp.toLocaleString() }}</span>
                </div>
                <div class="rc-term">
                  <span class="rc-term-k">{{ t('dungeons.firstClear') }}</span>
                  <span class="rc-term-v rc-term-v--coin"><img :src="runesIcon" alt="" class="rc-term-icon" />+{{ d.firstClearPremium }}</span>
                </div>
              </div>

              <button
                :disabled="ctaDisabled(d)"
                @click="begin(d.id)"
                class="btn-forge btn-forge--rune btn-forge--sm rc-cta"
              >{{ ctaLabel(d) }}</button>
            </div>
          </div>
        </article>
      </template>

      <!-- QA 18.08 item 12 — the course runs deeper than the catalogue.
           A sealed teaser stop at the foot of the list: warband raids with
           bosses, opened only from ground your clan actually holds. -->
      <div class="rc-chan" :style="{ '--dp': 1 }" aria-hidden="true"><span class="rc-boss"></span></div>
      <article class="rc-stop bound-frame surf-plank relative is-sealed rc-stop--soon" :style="{ '--dp': 1 }">
        <div class="rc-stop-body">
          <div class="rc-soon-kicker">{{ t('dungeons.soon.kicker') }}</div>
          <h2 class="rc-soon-title">{{ t('dungeons.soon.title') }}</h2>
          <p class="rc-soon-body">{{ t('dungeons.soon.body') }}</p>
        </div>
      </article>
    </div>

    <!-- INTERACTIVE TACTICAL ARENA — room-by-room, server-authoritative -->
    <FightScreen
      v-if="heistTarget"
      :heist-id="heistTarget"
      :hero-class="props.heroClass"
      :initial="heistInitial"
      @done="onHeistDone"
    />
  </section>
</template>

<style scoped>
/* ── THE SHIP'S LOG — the active run banner ─────────────────────────────────
   Kept as its own plank above the course (the crew's damage carries between
   rooms, so "what's live right now" earns a fixed spot rather than living
   inside the scrolling route). Retuned from a gold ticket-glow to ember: this
   is the one thing on the page that is "yours / live", and in this system
   that reading belongs to ember, not to gold (gold is coin/crest inlay only). */
.rc-log {
  padding: 1.15rem 1.4rem;
  animation: rc-log-glow 2.8s ease-in-out infinite;
}
@keyframes rc-log-glow {
  0%, 100% { box-shadow: 0 0 12px rgba(217, 135, 60, 0.12); }
  50% { box-shadow: 0 0 24px rgba(217, 135, 60, 0.3); }
}
.rc-log-kicker {
  font-family: var(--font-label);
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  color: #f0c9a3;
  margin-bottom: 3px;
}
.rc-log-title {
  font-family: var(--font-heading); font-size: 22px; line-height: 1;
  color: #e4ebf2;
  text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.85), 0 0 16px rgba(217, 135, 60, 0.2);
}
.rc-log-lbl {
  font-family: var(--font-label); font-size: 10px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #bccbdb;
}

/* room pips (log only) — aurora for progress, never gold: gold is coin, not
   light. Cleared rooms light aurora, the current room pulses. */
.rc-pip {
  width: 10px; height: 10px; transform: rotate(45deg); border-radius: 1.5px;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(127, 180, 216, 0.35);
}
.rc-pip-clear {
  background: linear-gradient(135deg, var(--color-aurora), var(--color-aurora-cool));
  border-color: transparent;
  box-shadow: 0 0 6px color-mix(in srgb, var(--color-aurora) 55%, transparent);
}
.rc-pip-now {
  background: rgba(111, 216, 192, 0.25);
  border-color: var(--color-aurora);
  animation: rc-pip-pulse 1.6s ease-in-out infinite;
}
@keyframes rc-pip-pulse {
  0%, 100% { box-shadow: 0 0 3px color-mix(in srgb, var(--color-aurora) 45%, transparent); }
  50% { box-shadow: 0 0 10px color-mix(in srgb, var(--color-aurora) 90%, transparent); }
}

/* ── THE COURSE ────────────────────────────────────────────────────────────
   ONE grid: `--chan-w` first column is the channel for every row on the page,
   so the line's x-position is one shared value rather than four independent
   guesses that happen to land close. `<template v-for>` puts the channel cell
   and the stop into the SAME grid as siblings, which is what makes them share
   a column track at all — two separate per-row grids would only agree by
   coincidence. */
.rc-route {
  --chan-w: 52px;
  position: relative;
  display: grid;
  grid-template-columns: var(--chan-w) 1fr;
  row-gap: 1.2rem;
  padding: 6px 0 10px;
}
/* the channel itself — unbroken from the first stop to the last, cold at the
   top, warming toward the foot exactly like the stops it threads through */
.rc-route::before {
  content: '';
  position: absolute;
  top: 10px; bottom: 10px;
  left: calc(var(--chan-w) / 2 - 1px);
  width: 2px;
  background: linear-gradient(180deg, var(--color-ice-dim) 0%, var(--color-iron-lit) 45%, var(--color-ember) 100%);
  opacity: 0.55;
  box-shadow: 0 0 6px rgba(207, 232, 255, 0.2);
  z-index: 0;
}
.rc-chan {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* the struck boss marking a stop on the channel — a genuine circle, carved
   twice per the convention: a dark groove, then a light burr 1px under it */
.rc-boss {
  position: relative;
  width: 20px; height: 20px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--color-ember) var(--dp), var(--color-ice-dim));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    inset 0 -2px 4px rgba(0, 0, 0, 0.45),
    0 0 0 3px var(--color-bg),
    0 0 9px color-mix(in srgb, var(--color-ember) var(--dp), rgba(127, 180, 216, 0.35));
}
.rc-boss::before, .rc-boss::after {
  content: '';
  position: absolute; inset: 0;
  border-radius: 50%;
  background: var(--orn-boss-dark) center / 14px 14px no-repeat;
}
.rc-boss::after {
  background-image: var(--orn-boss-lite);
  transform: translateY(1px);
  opacity: 0.55;
}
.rc-boss.is-here {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    inset 0 -2px 4px rgba(0, 0, 0, 0.45),
    0 0 0 3px var(--color-bg),
    0 0 13px color-mix(in srgb, var(--color-ember) 80%, transparent);
  animation: rc-boss-live 1.8s ease-in-out infinite;
}
@keyframes rc-boss-live {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.18); }
}

/* ── THE STOP ──────────────────────────────────────────────────────────────
   `bound-frame` gives the ice rim + iron straps; `surf-plank` gives it real
   timber instead of a flat colour. `--cb` (read by bound-frame) is retuned
   here from its cold default to the SAME danger tint as the boss on the
   channel, so a stop's own edge is part of the warm/cold reading, not just
   its scene. */
.rc-stop {
  --cb: color-mix(in srgb, var(--color-ember) var(--dp), var(--color-ice-dim));
  padding: 20px 16px 16px;
}
.rc-stop-body {
  display: flex;
  gap: 16px;
}
.rc-stop.is-here {
  --cb: var(--color-ember);
  animation: rc-stop-live 2.6s ease-in-out infinite;
}
/* the teaser stop at the foot of the course (item 12) — sealed, quiet, but
   clearly a stop on the same road, not an ad banner */
.rc-stop--soon { opacity: 0.75; }
.rc-stop--soon .rc-stop-body { display: block; }
.rc-soon-kicker {
  font-family: var(--font-label, sans-serif); text-transform: uppercase;
  letter-spacing: 0.09em; font-size: 10px; color: var(--color-gold, #c9a84c);
}
.rc-soon-title { font-family: var(--font-heading, serif); font-size: 20px; color: var(--color-text, #ece3cf); margin: 3px 0 4px; }
.rc-soon-body { font-family: var(--font-body, serif); font-style: italic; font-size: 13px; line-height: 1.5; color: var(--color-text-dim, #9a8f7a); max-width: 62ch; }
@keyframes rc-stop-live {
  0%, 100% { filter: drop-shadow(0 12px 26px rgba(0, 0, 0, 0.5)); }
  50% { filter: drop-shadow(0 12px 26px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 16px rgba(217, 135, 60, 0.4)); }
}

/* the scene: heistArt in a hand-hewn frame, never full-bleed under the whole
   stop. Saturation itself carries part of the warm/cold read — a safe early
   raid's plate sits duller and cooler, a deadly one further down the course
   is lit full and warm — so the difference lives in the painted scene, not a
   CSS tint painted over a blank box. */
.rc-scene {
  position: relative;
  flex: 0 0 216px;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  clip-path: var(--clip-hewn);
  box-shadow: var(--rim-ice-sunken);
  background: linear-gradient(180deg, #1a2432, #0e1620);
}
.rc-scene img {
  width: 100%; height: 100%; object-fit: cover; display: block;
  filter: saturate(calc(55% + var(--dp) * 0.5)) brightness(calc(94% + var(--dp) * 0.09));
}
/* the scrim: transparent through the top 40%, dark by the foot, so the name
   riding over it always wins regardless of what the plate is painted with */
.rc-scene-scrim {
  position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, transparent 40%, rgba(8, 14, 24, 0.78) 100%);
}
/* the ember pool at a dangerous stop's foot — invisible at the cold end of the
   course (`--dp: 0%` collapses its own opacity), a real bloom at the deadly end */
.rc-pool {
  position: absolute; inset: auto 0 0 0; height: 55%; pointer-events: none;
  opacity: var(--dp);
  background: radial-gradient(70% 100% at 50% 100%, color-mix(in srgb, var(--color-ember) 60%, transparent), transparent 75%);
}
.rc-rooms {
  position: absolute; top: 7px; left: 7px; z-index: 2;
  font-family: var(--font-label); font-size: 9px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #e4ebf2;
  background: rgba(8, 6, 14, 0.82);
  border: 1px solid rgba(207, 232, 255, 0.22);
  padding: 3px 7px;
}
.rc-name {
  position: absolute; left: 10px; right: 10px; bottom: 8px; z-index: 2;
  font-family: var(--font-heading);
  font-size: clamp(18px, 1.7vw, 22px); line-height: 1.08;
  color: #f0f4f9;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.95), 0 0 20px rgba(0, 0, 0, 0.7), 0 -1px 0 rgba(0, 0, 0, 0.9);
}

/* ── sealed (below the level gate) — frost, matching the ward frieze's
   "unknown" treatment: cold, unreadable, deliberately receding ────────────── */
.rc-frost {
  position: absolute; inset: 0; z-index: 3; pointer-events: none;
  background:
    var(--orn-frost-ice) repeat-x left top,
    linear-gradient(150deg, transparent 30%, rgba(224, 242, 255, 0.16) 42%, transparent 48%),
    linear-gradient(180deg, rgba(150, 196, 232, 0.08), rgba(120, 172, 214, 0.24));
  box-shadow: inset 0 0 20px rgba(224, 242, 255, 0.14);
}
.is-sealed { filter: saturate(0.55) brightness(0.85); }
.is-sealed .rc-name { opacity: 0.75; }

/* ── smoldering (post-clear cooldown) — the same door, its embers dying down,
   which is what the CTA's own copy already says ("Still smoldering") ───────── */
.rc-embers {
  position: absolute; inset: 0; z-index: 3; pointer-events: none;
  background: radial-gradient(60% 55% at 50% 105%, rgba(217, 135, 60, 0.22), transparent 70%);
  mix-blend-mode: soft-light;
}
.is-smoldering { filter: saturate(0.8) brightness(0.9); }

.rc-info {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.rc-blurb {
  font-family: var(--font-body); font-style: italic; font-size: 13px; line-height: 1.4;
  color: #bccbdb;
  margin-bottom: 10px;
}

/* the terms — label left, value right, on ONE shared axis: every row uses the
   same `justify-content: space-between` rule rather than a hand-placed value
   column, so the numbers line up because the rule is shared, not tuned per row. */
.rc-terms {
  display: flex; flex-direction: column;
  margin-bottom: 12px;
}
.rc-term {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px;
  padding: 4px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.4);
  box-shadow: inset 0 1px 0 rgba(207, 232, 255, 0.06);
}
.rc-term-k {
  font-family: var(--font-label); font-size: 9.5px; letter-spacing: 0.07em; text-transform: uppercase;
  color: #bccbdb;
}
.rc-term-v {
  display: inline-flex; align-items: center; gap: 5px;
  font-family: var(--font-heading); font-size: 14px;
  color: #cfdae8;
  font-variant-numeric: tabular-nums;
}
.rc-term-v--coin { color: var(--color-gold-bright); }
.rc-term-icon { width: 13px; height: 13px; object-fit: contain; }

.rc-cta { margin-top: auto; align-self: stretch; }

@media (max-width: 680px) {
  .rc-route { --chan-w: 34px; }
  .rc-stop-body { flex-direction: column; }
  .rc-scene { flex-basis: auto; aspect-ratio: 16 / 10; }
}
/* Phone: even a 34px channel starves the raid cards — their carved corner
   frames were kissing the right screen edge. The route line bows out; the
   cards take the full width. */
@media (max-width: 480px) {
  .rc-route { --chan-w: 0px; grid-template-columns: minmax(0, 1fr); row-gap: 1rem; }
  .rc-route::before, .rc-chan { display: none; }
}

@media (prefers-reduced-motion: reduce) {
  .rc-log, .rc-pip-now, .rc-boss.is-here, .rc-stop.is-here { animation: none; }
}
</style>
